import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { GenericToggle } from "@components/FormsV2/GenericToggle";
import { useV2ProjectMergeDataQuery } from "@generated";
import MergeDataTree from "./components/MergeDataTree";
import {
  buildMergeDataIndex,
  expandableIds,
  MergeDataNode,
  searchMergeData,
} from "./utils/mergeDataIndex";
import { useStyles } from "./styles";

type Version = "1" | "2";

interface Props {
  projectId: string;
}

interface Toast {
  key: number;
  variant: "success" | "danger";
  text: string;
}

const SEARCH_DEBOUNCE_MS = 200;
const MIN_QUERY = 2;

// The merge-data action renders its 422 as { errors: { title, detail } } --
// an object, not the JSON:API array -- so read both shapes.
const errorMessage = (error: unknown): string => {
  const { status, data } = (error ?? {}) as { status?: number; data?: any };
  if (status === 403) return "You don't have permission to view merge data.";
  if (status === 404) return "This project could not be found.";
  if (status === 422) {
    const detail = data?.errors?.detail ?? data?.errors?.[0]?.detail;
    return detail
      ? `Merge data could not be built: ${detail}`
      : "Merge data could not be built.";
  }
  return "Merge data could not be loaded.";
};

const MergeData = ({ projectId }: Props) => {
  const styles = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const version: Version = searchParams.get("version") === "2" ? "2" : "1";

  const { currentData, isLoading, isFetching, error } =
    useV2ProjectMergeDataQuery(
      { id: projectId, filter: { version } },
      { refetchOnMountOrArgChange: true }
    );

  const index = useMemo(
    () => buildMergeDataIndex(currentData?.data?.attributes?.content),
    [currentData]
  );

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [searchExpanded, setSearchExpanded] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => setExpandedIds(new Set()), [version]);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(input.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const search = useMemo(
    () => (query.length >= MIN_QUERY ? searchMergeData(index, query) : null),
    [index, query]
  );

  useEffect(() => {
    if (search) setSearchExpanded(new Set(search.ancestorIds));
  }, [search]);

  const activeExpanded = search ? searchExpanded : expandedIds;
  const setActiveExpanded = search ? setSearchExpanded : setExpandedIds;

  const toggle = useCallback(
    (id: string) =>
      setActiveExpanded((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [setActiveExpanded]
  );

  const copy = useCallback(async (node: MergeDataNode) => {
    const key = Date.now();
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(node.templatePath);
      setToast({
        key,
        variant: "success",
        text: `Copied ${node.templatePath}`,
      });
    } catch {
      setToast({
        key,
        variant: "danger",
        text: `Could not copy ${node.templatePath}`,
      });
    }
  }, []);

  const matchCount = search?.matchIds.size ?? 0;
  const loading = isLoading || (isFetching && !currentData);

  return (
    <div className={styles.wrapper}>
      {toast && (
        <ToastAlert
          key={toast.key}
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
      <Row>
        <Col>
          <div className={styles.header}>
            <h1 className={styles.title}>Merge Data</h1>
            <div className={styles.versionSwitch}>
              <span>V1</span>
              {/* GenericToggle keeps its own state; key on the version so
                  back/forward navigation resyncs it with the URL. */}
              <GenericToggle
                key={version}
                initialValueOfIsOn={version === "2"}
                onToggle={(isOn) =>
                  setSearchParams({ version: isOn ? "2" : "1" })
                }
                onText="V2"
                offText="V1"
                hasText={false}
              />
              <span>V2</span>
            </div>
          </div>
        </Col>
        <hr className={styles.line} />
      </Row>
      <Row>
        <Col>
          <Card className={styles.card}>
            {loading ? (
              <ScopeStackSpinner />
            ) : error ? (
              <Alert variant="danger">{errorMessage(error)}</Alert>
            ) : index.rootIds.length === 0 ? (
              <p className={styles.muted}>No merge data.</p>
            ) : (
              <>
                <div className={styles.toolbar}>
                  <Form.Control
                    className={styles.search}
                    type="search"
                    aria-label="Search merge data"
                    placeholder="Search keys and values"
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                  />
                  {search && (
                    <span className={styles.muted}>
                      {matchCount === 1 ? "1 match" : `${matchCount} matches`}
                    </span>
                  )}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() =>
                      setActiveExpanded(new Set(expandableIds(index)))
                    }
                  >
                    Expand all
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setActiveExpanded(new Set())}
                  >
                    Collapse all
                  </Button>
                  <span className={styles.muted}>
                    Click a value to copy its template path.
                  </span>
                </div>
                <MergeDataTree
                  index={index}
                  expandedIds={activeExpanded}
                  onToggle={toggle}
                  onCopy={copy}
                  search={search}
                />
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MergeData;
